import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { ForecastModal } from '../../components/custom_components/ForecastModal'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import PerText from '../../components/custom_components/Text'
import { useCollapse } from '../../routes/collapse-context'
import useRankingNetworkCompanyCountryYearSelector from '../cultivatingTalents/useRankingNetworkCompanyCountryYearSelector'
import { PyramidsHeader } from './PyramidsHeader'
import { usePyramidsStore } from './PyramidsStore'
import { PyramidsTabs } from './PyramidsTabs'
import { RoleName } from '../../types'
import { useAuth } from '../../context/auth-context'
import { useAreAgeAndCareerPathVisible } from './useAreAgeAndCareerPathVisible'

export const PyramidsView: FC = () => {
  const [t] = useTranslation()
  const [initStore, storeInitialized] = usePyramidsStore((state) => [state.initStore, state.storeInitialized], shallow)
  const collapsed = useCollapse()
  const { user } = useAuth()

  const { data, error, loading } = useRankingNetworkCompanyCountryYearSelector()

  useEffect(() => {
    if (!!data && !!data.networksCompaniesCountriesYears && data.networksCompaniesCountriesYears.length) {
      initStore(data.networksCompaniesCountriesYears)
    }
  }, [data, initStore])

  const hasRightsToSeePyramids = () => {
    if (!user || !user.roles || user.roles.length === 0) {
      return false
    }
    const allowedRoles: RoleName[] = [
      RoleName.NetworkManager,
      RoleName.ClusterManager,
      RoleName.CompanyManager,
      RoleName.BusinessPartner,
      RoleName.Hr,
    ]
    for (const userRole of user.roles) {
      if (allowedRoles.includes(userRole)) {
        return true
      }
    }
    return false
  }

  if (
    loading ||
    error ||
    !data ||
    !data.networksCompaniesCountriesYears ||
    (!!data.networksCompaniesCountriesYears.length && !storeInitialized)
  ) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors
          isLoading={loading}
          hasNoData={!data || !data.networksCompaniesCountriesYears}
          error={error}
          isPermissionDenied={!error && !hasRightsToSeePyramids}
        />
      </ContentContainer>
    )
  } else if (data.networksCompaniesCountriesYears.length === 0) {
    return (
      <PerInfoBox>
        <InfoBoxTile
          heading={t('pyramidsView.empty.heading')}
          headingAlign="center"
          contentAlign="center"
          headingColor="secondary"
          headingMt="xl"
        >
          <PerText>{t('pyramidsView.empty.message')}</PerText>
        </InfoBoxTile>
      </PerInfoBox>
    )
  } else {
    return <PyramidsViewContent />
  }
}

export const PyramidsViewContent: FC = () => {
  const [selected, setIsAgePyramidVisible, setIsCareerPathEnabled] = usePyramidsStore(
    (state) => [state.selected, state.setIsAgePyramidVisible, state.setIsCareerPathEnabled],
    shallow,
  )
  const collapsed = useCollapse()

  const { data, error, loading } = useAreAgeAndCareerPathVisible(selected.country)

  useEffect(() => {
    if (!!data) {
      setIsAgePyramidVisible(data.isAgePyramidVisible)
      setIsCareerPathEnabled(data.isCareerPathEnabled)
    }
  }, [data, setIsAgePyramidVisible, setIsCareerPathEnabled])

  if (!data || error || loading) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
      </ContentContainer>
    )
  } else {
    return (
      <ContentContainer collapsed={collapsed}>
        <PyramidsHeader />
        <PyramidsTabs />
        <ForecastModal />
      </ContentContainer>
    )
  }
}
